import React, { useState } from 'react';
import {
  Typography,
  Box,
  Card,
  Stack,
  Chip,
  Fab,
  Popover,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../../constants/icons';
import { useMasterData } from '../../providers/MasterDataProvider';
import ProfitAndLossSearchFilters, { ProfitAndLossFilterValues } from '../ProfitAndLossSearchFilters';
import { STANDARD_FAB_CTA_SX } from '../../constants/buttonStyles';
import { generateFiscalYearOptions, PERFORMANCE_RANGE_OPTIONS, calculateCurrentFiscalYear } from './constants';
import type { UseProfitAndLossResult } from '../../hooks/useProfitAndLoss';

type Props = {
  pl: UseProfitAndLossResult;
};

/**
 * 損益ページ共通の見出し＋絞り込みヘッダー。
 * 現在の絞り込み条件（年度・予算・拠点・部署）をChipで要約表示し、Fabから検索Popoverを開く。
 */
export default function ProfitAndLossFilterHeader({ pl }: Props) {
  const { branchList, departmentList } = useMasterData();
  // 検索フィルター Popover のアンカー
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  // 適用中の保存条件はフック側（pl）で保持する（ローディング中の再マウントで消えないように）
  const { appliedFilterName, setAppliedFilterName, appliedFilterId, setAppliedFilterId } = pl;

  // 「適用中」Chip の × — 絞り込みをデフォルト（今年度・先頭予算パターン・全拠点・全部署）へ戻し、
  // 適用中表示も解除する。setter 変更で再フェッチが走る。
  const handleResetToDefault = () => {
    const firstVersion = pl.budgetVersions.length > 0 ? pl.budgetVersions[0].version_code : '';
    pl.setFiscalYear(calculateCurrentFiscalYear());
    pl.setSelectedBudgetVersion(firstVersion);
    pl.setSelectedBranchCodes([]);
    pl.setSelectedDepartmentCodes([]);
    pl.setBranchDeptLogic('and');
    setAppliedFilterName(null);
    setAppliedFilterId(null);
  };

  // 選択中の業績範囲ラベル (見出し表示用)。単月は「N月度」、それ以外は範囲名で表記する
  const isMonth = pl.performanceRange === 'month';
  const rangeLabel = PERFORMANCE_RANGE_OPTIONS.find(o => o.value === pl.performanceRange)?.label ?? '単月';
  const periodSummary = isMonth ? `${pl.currentMonth}月度` : `${pl.fiscalYear}年度 ${rangeLabel}`;
  const periodTarget = isMonth ? `表示対象月（${pl.currentMonth}月）` : `表示対象期間（${rangeLabel}）`;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}>
          <Icons.Assessment /> 経営概況詳細 <span style={{ fontSize: '18px', fontWeight: 500, color: '#64748b' }}>- {periodSummary} 実績 vs 予算</span>
        </Typography>
        <Typography variant="body2" sx={{ color: '#475569', mb: 0 }}>
          {periodTarget}の勘定科目別 実績・予算対比
        </Typography>
      </Box>

      {/* 絞り込み条件サマリー + 検索フィルター起動ボタン */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.5 }}>
          {appliedFilterName ? (
            <Chip
              size="small"
              icon={<Icons.Bookmark sx={{ fontSize: 14 }} />}
              label={`適用中: ${appliedFilterName}`}
              color="primary"
              variant="outlined"
              onDelete={handleResetToDefault}
              sx={{ fontSize: '11px', height: '22px', fontWeight: 700 }}
            />
          ) : (
            <Chip size="small" label="条件指定なし" variant="outlined" sx={{ fontSize: '11px', height: '22px', color: '#94a3b8', borderColor: '#e2e8f0' }} />
          )}
          <Chip size="small" label={`${pl.fiscalYear}年度`} sx={{ fontSize: '11px', height: '22px', bgcolor: '#eff6ff', color: '#1e3a8a', fontWeight: 600 }} />
          {(() => {
            const name = pl.budgetVersions.find(v => v.version_code === pl.selectedBudgetVersion)?.version_name;
            return name ? (
              <Chip size="small" label={name} sx={{ fontSize: '11px', height: '22px', bgcolor: '#f5f3ff', color: '#6d28d9', fontWeight: 600 }} />
            ) : null;
          })()}
          <Chip size="small" label={pl.selectedBranchCodes.length === 0 ? '全拠点' : `拠点 ${pl.selectedBranchCodes.length}件`} sx={{ fontSize: '11px', height: '22px' }} />
          {/* 拠点・部署の両方を指定しているときだけ結合ロジック (かつ/または) を表示する */}
          {pl.selectedBranchCodes.length > 0 && pl.selectedDepartmentCodes.length > 0 && (
            <Chip
              size="small"
              label={pl.branchDeptLogic === 'or' ? 'または' : 'かつ'}
              sx={{ fontSize: '11px', height: '22px', fontWeight: 700, bgcolor: pl.branchDeptLogic === 'or' ? '#fff7ed' : '#f0fdf4', color: pl.branchDeptLogic === 'or' ? '#c2410c' : '#15803d' }}
            />
          )}
          <Chip size="small" label={pl.selectedDepartmentCodes.length === 0 ? '全部署' : `部署 ${pl.selectedDepartmentCodes.length}件`} sx={{ fontSize: '11px', height: '22px' }} />
        </Stack>
        <Fab
          variant="extended"
          size="small"
          onClick={(e) => setFilterAnchorEl(e.currentTarget)}
          sx={STANDARD_FAB_CTA_SX}
        >
          <Icons.FilterAltOutlined />
          絞り込み
        </Fab>
      </Box>

      {/* 🔍 検索フィルター Popover (会計年度・予算パターン・拠点・部署を指定して実行) */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #edf2f7',
          },
        }}
      >
        <Card sx={{ width: '520px', border: 'none', boxShadow: 'none' }}>
          <Box sx={{ p: 1, borderBottom: '1px solid #eee', bgcolor: '#f1f5f9' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155', fontSize: '13px' }}>
              損益を検索・絞り込み
            </Typography>
          </Box>
          <ProfitAndLossSearchFilters
            fiscalYear={pl.fiscalYear}
            budgetVersionCode={pl.selectedBudgetVersion}
            branchCodes={pl.selectedBranchCodes}
            departmentCodes={pl.selectedDepartmentCodes}
            branchDeptLogic={pl.branchDeptLogic}
            fiscalYearOptions={generateFiscalYearOptions()}
            budgetVersions={pl.budgetVersions}
            branchList={branchList}
            departmentList={departmentList}
            savedFilters={pl.savedFilters}
            appliedFilterId={appliedFilterId}
            onApply={(v: ProfitAndLossFilterValues, appliedName?: string | null, appliedId?: string | null) => {
              pl.setFiscalYear(v.fiscalYear);
              pl.setSelectedBudgetVersion(v.budgetVersionCode);
              pl.setSelectedBranchCodes(v.branchCodes);
              pl.setSelectedDepartmentCodes(v.departmentCodes);
              pl.setBranchDeptLogic(v.branchDeptLogic ?? 'and');
              setAppliedFilterName(appliedName ?? null);
              setAppliedFilterId(appliedId ?? null);
              setFilterAnchorEl(null);
            }}
            onAppliedRemoved={() => { setAppliedFilterName(null); setAppliedFilterId(null); }}
          />
        </Card>
      </Popover>
    </Box>
  );
}
